import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EsimOrder } from "../../models/EsimOrder";
import {
  fetchEsimOrders as fetchEsimOrdersApi,
  createEsimOrder as createEsimOrderApi,
  updateEsimOrder as updateEsimOrderApi,
  deleteEsimOrder as deleteEsimOrderApi,
} from "../../services/apiConfig";
import type { ListParams } from "../../services/api";

interface EsimOrdersState {
  esimOrders: EsimOrder[];
  /** The current page only - the server no longer sends the whole table. */
  total: number;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: EsimOrdersState = {
  esimOrders: [],
  total: 0,
  loading: "idle",
  error: null,
};

export const fetchEsimOrders = createAsyncThunk(
  "esimOrders/fetchEsimOrders",
  async (params?: ListParams) => {
    const response = await fetchEsimOrdersApi(params);
    return response;
  },
);

export const addEsimOrder = createAsyncThunk(
  "esimOrders/addEsimOrder",
  async (esimOrder: Omit<EsimOrder, "id" | "insert_date_time">) => {
    const response = await createEsimOrderApi(esimOrder);
    return response;
  },
);

export const editEsimOrder = createAsyncThunk(
  "esimOrders/editEsimOrder",
  async (payload: { id: number; data: Partial<EsimOrder> }) => {
    const response = await updateEsimOrderApi(payload.id, payload.data);
    return response;
  },
);

export const removeEsimOrder = createAsyncThunk(
  "esimOrders/removeEsimOrder",
  async (id: number) => {
    await deleteEsimOrderApi(id);
    return id;
  },
);

const esimOrderSlice = createSlice({
  name: "esimOrders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch eSIM orders
      .addCase(fetchEsimOrders.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchEsimOrders.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.esimOrders = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(fetchEsimOrders.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch eSIM orders";
      })
      // Add eSIM order
      .addCase(addEsimOrder.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(addEsimOrder.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.esimOrders.unshift(action.payload);
      })
      .addCase(addEsimOrder.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to create eSIM order";
      })
      // Edit eSIM order
      .addCase(editEsimOrder.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(editEsimOrder.fulfilled, (state, action) => {
        state.loading = "succeeded";
        const index = state.esimOrders.findIndex(
          (order) => order.id === action.payload.id,
        );
        if (index !== -1) {
          state.esimOrders[index] = action.payload;
        }
      })
      .addCase(editEsimOrder.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to update eSIM order";
      })
      // Remove eSIM order
      .addCase(removeEsimOrder.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(removeEsimOrder.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.esimOrders = state.esimOrders.filter(
          (order) => order.id !== action.payload,
        );
      })
      .addCase(removeEsimOrder.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to delete eSIM order";
      });
  },
});

export default esimOrderSlice.reducer;
